import { useState, useCallback, useEffect } from 'react';
import { Modal } from 'antd';

import { useIntl } from 'react-intl';
import { LOCALE } from 'constants/intl-wrapper';
import { mediaHook } from 'customHooks';

import { useModal } from 'contexts/modalProvider.context';
import { CONNECT_PLUGIN_MODAL } from 'constants/connect-plugin';
import { INSTAGRAM } from 'constants/chats';

import BRButton from 'components/BRButton/BRButton';
import PopupGuideModal from 'components/Chats/components/PopupGuideModal/PopupGuideModal';
import ConnectionFirstStep from '../components/ConnectionFirstStep/ConnectionFirstStep';
import ConnectionSecondStep from '../components/ConnectionSecondStep/ConnectionSecondStep';
import InstagramSectionTwo from './InstagramSectionTwo/InstagramSectionTwo';

import { ReactComponent as Close } from 'assets/bosta-icons/Close.svg';
import { ReactComponent as ArrowLeft } from 'assets/bosta-icons/Arrow-left.svg';
import { ReactComponent as RightIcon } from 'assets/bosta-icons/Arrow-right.svg';

import './SetupConnectionModal.less';

const SetupConnectionModal = ({
  subtitle,
  apiKey,
  setBusinessInfo,
  isActivated,
  name,
  handleLoginToMeta,
  setIsConnectionFailed,
  platform,
  close,
  mobileScreenSizes,
  setLoggedPlatform,
  isPopupBlocked,
  ...props
}) => {
  const intl = useIntl();
  const { openModal } = useModal();
  const { formatMessage } = intl;
  const { isSmallMobileScreen } = mobileScreenSizes;

  const [step, setStep] = useState(0);
  const [pixelValue, setPixelValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({ isVisible: false });

  const IsInstagramConnection = platform?.keyName === INSTAGRAM;

  useEffect(() => {
    setPixelValue(isActivated);
  }, [isActivated]);

  const handleModalAction = useCallback(() => {
    if (IsInstagramConnection ? step === 2 : step === 1) {
      if (isPopupBlocked) {
        handleOpenPopupModal();
        close();
      } else {
        setIsLoading(true);

        setTimeout(() => {
          handleLoginToMeta(
            platform.configId,
            platform.keyName,
            setLoggedPlatform
          )
            .then(() => {
              setIsConnectionFailed(false);
            })
            .catch((error) => {
              setIsConnectionFailed(true);
            });
          setIsLoading(false);
          close();
        }, 1000);
      }
    } else {
      setStep(step + 1);
    }
  }, [step, handleLoginToMeta, close, isSmallMobileScreen]);

  const handleOpenPopupModal = () => {
    openModal(PopupGuideModal, {});
  };

  const handleBackStep = () => {
    setStep(step - 1);
  };

  const arrowBasedOnLanguage =
    intl.locale === LOCALE.EN ? (
      <ArrowLeft onClick={handleBackStep} />
    ) : (
      <RightIcon onClick={handleBackStep} />
    );

  const RenderModalFooter = (
    <>
      <BRButton
        label={formatMessage({ id: 'common.cancel' })}
        type="outline"
        onClick={close}
      />

      <BRButton
        label={formatMessage({
          id: `common.${
            !step || (IsInstagramConnection && step === 1) ? 'next' : 'connect'
          }`
        })}
        onClick={handleModalAction}
        loading={isLoading}
        disabled={step && isLoading}
        type="primary"
      />
    </>
  );

  return (
    <Modal
      {...props}
      footer={RenderModalFooter}
      onCancel={close}
      maskClosable={false}
      width={null}
      closeIcon={<Close />}
      wrapClassName="br-setup-connection-modal"
      title={
        <div className="br-setup-connection-modal__header">
          {!!step && arrowBasedOnLanguage}
          <div className="br-setup-connection-modal__header__content">
            <span className="display-xs">
              {formatMessage({
                id: `${
                  CONNECT_PLUGIN_MODAL[platform?.keyName?.toUpperCase()].title
                }`
              })}
            </span>
            <span className="body text-gray">
              {formatMessage({
                id: `chats_plugins.install_plugins.autmate_chat`
              })}
            </span>
          </div>
        </div>
      }
    >
      {step === 0 ? (
        <ConnectionFirstStep
          intl={intl}
          name={name}
          platform={platform}
          {...props}
        />
      ) : step === 1 && IsInstagramConnection ? (
        <InstagramSectionTwo platform={platform} />
      ) : (
        <>
          <ConnectionSecondStep
            intl={intl}
            data={{
              pixelValue,
              setPixelValue,
              setErrors,
              errors,
              apiKey,
              setBusinessInfo,
              isActivated,
              intl,
              name,
              platform,
              isPopupBlocked,
              ...props
            }}
          />
        </>
      )}
    </Modal>
  );
};

export default mediaHook(SetupConnectionModal);
